import { useState, useContext } from 'react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { getExerciseInfo } from 'functions/assessmentSummaryUtils'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'

const BAR_PARENT_HEIGHT = 8
const BAR_HEIGHT = 8

const MINI_BAR_PARENT_HEIGHT = 5
const MINI_BAR_HEIGHT = 4

export default function AggregateAssessmentSummaryExerciseSetListItem({
    selectedSide,
    exerciseId,
    exerciseName,
    exerciseLateral,
    percentageScore,
    individualCompletionPercentages
}) {

    const { ShowModal } = useContext(ModalContext)

    const [isExpanded, setIsExpanded] = useState(false)

    const ShowInfoModal = () => {

        const exerciseInfo = getExerciseInfo(exerciseId)

        const modalData = {
            key: ModalKey.ExerciseAssessmentInfo,
            fullscreen: true,
            exerciseName: exerciseName,
            exerciseLateral: exerciseLateral,
            // currentSet: currentSet,
            exerciseInfo: exerciseInfo
        }

        ShowModal(modalData)
    }

    return (

        <div className="py-1">

            {/* <pre className="text-xs p-2 bg-gray-100 border-b border-gray-400 overflow-x-auto">
                {JSON.stringify(percentageScore, null, 2)}
            </pre> */}

            {/* Exercise Name */}
            <div 
                className="w-full flex justify-center items-center text-gray-600 pr-7 mb-1 cursor-pointer"
                onClick={() => ShowInfoModal()}
            >

                <InformationCircleIcon className="w-4 h-4 mr-2 text-gray-500" />

                <div className="text-center">{exerciseName}</div>

                {individualCompletionPercentages.length > 0 && (
                    <div className="text-xs text-gray-600 ml-2">
                        ({individualCompletionPercentages.length} athletes)
                    </div>
                )}

            </div>

            {/* Clickable Bar Section */}
            <div 
                onClick={() => setIsExpanded(!isExpanded)}
                className="relative cursor-pointer group"
            >
                <div className="flex items-center gap-x-2">

                    <div className={`relative h-${BAR_PARENT_HEIGHT} flex-grow`}>
                        
                        <div className={`absolute w-full h-${BAR_HEIGHT} top-1/2 -translate-y-1/2 bg-gray-300 rounded-lg`} />
                        
                        <div className="absolute left-1/2 h-full w-[2px] bg-white z-10" />

                        {exerciseLateral === 'unilateral' && 
                            <>

                                {/* Bars */}

                                <div 
                                    className={`
                                        absolute top-1/2 right-[50%] h-${BAR_HEIGHT} -translate-y-1/2 rounded-l-lg
                                        ${selectedSide === 'left' ? 'bg-orange-400' : 'bg-orange-300'}
                                    `}
                                    style={{
                                        width: `${percentageScore.leftSide / 2}%`
                                    }}
                                />
                                
                                <div 
                                    className={`
                                        absolute top-1/2 left-[50%] h-${BAR_HEIGHT} -translate-y-1/2 rounded-r-lg
                                        ${selectedSide === 'right' ? 'bg-orange-400' : 'bg-orange-300'}
                                    `}
                                    style={{
                                        width: `${percentageScore.rightSide / 2}%`
                                    }}
                                />

                                {/* Text */}

                                <div
                                    className="absolute top-1/2 left-[35%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-right w-[20%]"
                                    style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                >
                                    {Math.round(percentageScore.leftSide)}%
                                </div>

                                <div
                                    className="absolute top-1/2 left-[65%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-left w-[20%]"
                                    style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                >
                                    {Math.round(percentageScore.rightSide)}%
                                </div>
                            </>
                        }

                        {exerciseLateral === 'bilateral' && 
                            <>

                                {/* Bars */}

                                <div 
                                    className={`
                                        absolute top-1/2 right-[50%] h-${BAR_HEIGHT} -translate-y-1/2 rounded-l-lg
                                        ${selectedSide === 'left' ? 'bg-orange-400' : 'bg-orange-300'}
                                    `}
                                    style={{
                                        width: `${Math.round(percentageScore / 2)}%`                                
                                    }}
                                />
                                
                                <div 
                                    className={`
                                        absolute top-1/2 left-[50%] h-${BAR_HEIGHT} -translate-y-1/2 rounded-r-lg
                                        ${selectedSide === 'right' ? 'bg-orange-400' : 'bg-orange-300'}
                                    `}
                                    style={{
                                        width: `${Math.round(percentageScore / 2)}%`
                                    }}
                                />

                                {/* Text */}

                                <div
                                    className="absolute top-1/2 left-[35%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-right w-[20%]"
                                    style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                >
                                    {Math.round(percentageScore)}%
                                </div>

                                <div
                                    className="absolute top-1/2 left-[65%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-left w-[20%]"
                                    style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                >
                                    {Math.round(percentageScore)}%
                                </div>
                            </>
                        }
                    </div>

                    {/* Chevron Icon */}
                    <div className="text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0">
                        {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5 transform transition-transform duration-200" />
                        ) : (
                            <ChevronDownIcon className="h-5 w-5 transform transition-transform duration-200" />
                        )}
                    </div>

                </div>
            </div>
            
            {/* Expandable Section */}
            <div className={`
                overflow-hidden transition-all duration-200 ease-in-out -mt-1
                ${isExpanded ? 'opacity-100' : 'max-h-0 opacity-0'}
            `}>
                <div className="p-2 bg-gray-100 border-x border-b border-gray-300 rounded-b-lg mr-7 space-y-1">
                    {individualCompletionPercentages?.map((individualRecord, index) => (
                        <div key={index}>

                            {/* <pre className="text-xs p-2 bg-gray-100 border-b border-gray-400 overflow-x-auto">
                                {JSON.stringify(individualRecord, null, 2)}
                            </pre> */}

                            <div className="text-xs text-gray-500 z-20">{individualRecord.firstName} {individualRecord.lastName} ({individualRecord.email})</div>
                        
                            <div className={`relative h-${MINI_BAR_PARENT_HEIGHT} flex-grow`}>

                                <div className={`absolute w-full h-${MINI_BAR_HEIGHT} top-1/2 -translate-y-1/2 bg-gray-300 rounded-lg`} />

                                <div className="absolute left-1/2 h-full w-[2px] bg-white z-10" />

                                {individualRecord.exerciseLateral === 'unilateral' && 
                                    <>
                                        <div 
                                            className={`
                                                absolute top-1/2 right-[50%] h-${MINI_BAR_HEIGHT} -translate-y-1/2 rounded-l-lg
                                                ${selectedSide === 'left' ? 'bg-orange-400' : 'bg-orange-300'}
                                            `}
                                            style={{
                                                width: `${individualRecord.percentageScore.leftSide / 2}%`
                                            }}
                                        />
                                        
                                        <div 
                                            className={`
                                                absolute top-1/2 left-[50%] h-${MINI_BAR_HEIGHT} -translate-y-1/2 rounded-r-lg
                                                ${selectedSide === 'right' ? 'bg-orange-400' : 'bg-orange-300'}
                                            `}
                                            style={{
                                                width: `${individualRecord.percentageScore.rightSide / 2}%`
                                            }}
                                        />

                                        <div
                                            className="absolute top-1/2 left-[35%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-right w-[20%]"
                                            style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                        >
                                            {Math.round(individualRecord.percentageScore.leftSide)}%
                                        </div>

                                        <div
                                            className="absolute top-1/2 left-[65%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-left w-[20%]"
                                            style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                        >
                                            {Math.round(individualRecord.percentageScore.rightSide)}%
                                        </div>
                                    </>
                                }

                                {individualRecord.exerciseLateral === 'bilateral' && 
                                    <>
                                        <div 
                                            className={`
                                                absolute top-1/2 right-[50%] h-${MINI_BAR_HEIGHT} -translate-y-1/2 rounded-l-lg
                                                ${selectedSide === 'left' ? 'bg-orange-400' : 'bg-orange-300'}
                                            `}
                                            style={{
                                                width: `${Math.round(individualRecord.percentageScore / 2)}%`                                
                                            }}
                                        />
                                        
                                        <div 
                                            className={`
                                                absolute top-1/2 left-[50%] h-${MINI_BAR_HEIGHT} -translate-y-1/2 rounded-r-lg
                                                ${selectedSide === 'right' ? 'bg-orange-400' : 'bg-orange-300'}
                                            `}
                                            style={{
                                                width: `${Math.round(individualRecord.percentageScore / 2)}%`
                                            }}
                                        />

                                        <div
                                            className="absolute top-1/2 left-[35%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-right w-[20%]"
                                            style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                        >
                                            {Math.round(individualRecord.percentageScore)}%
                                        </div>

                                        <div
                                            className="absolute top-1/2 left-[65%] -translate-x-1/2 -translate-y-1/2 text-white text-xs font-bold z-10 text-left w-[20%]"
                                            style={{ textShadow: '0.5px 0.5px 0 #111, -0.5px -0.5px 0 #111, 0.5px -0.5px 0 #111, -0.5px 0.5px 0 #111, 0.5px 0.5px 0 #111' }}
                                        >
                                            {Math.round(individualRecord.percentageScore)}%
                                        </div>
                                    </>
                                }

                            </div>

                        </div>
                    )) || (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center bg-gray-50">
                            No athlete data available
                        </div>
                    )}
                </div>

            </div>

        </div>
    )

}